import { useMemo } from 'react';
import { Box, SxProps, useTheme } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Product } from '@/types/ProductType';

import 'swiper/css';
import 'swiper/css/pagination';

import { Pagination } from 'swiper/modules';

import { productPhotos } from '@/utils/propertyEnrichment';
import { CommonImage } from '../Common/CommonImage';

type ProductCardImageProps = {
  product?: Product;
  sx?: SxProps;
  /** Overlay a "gathering details" note while photos may still arrive. */
  pending?: boolean;
};

export const ProductCardImage = ({
  product,
  sx,
  pending = false,
}: ProductCardImageProps) => {
  const theme = useTheme();

  // At least one slide, so a property without photos shows the placeholder.
  const photos = useMemo(() => {
    const list = productPhotos(product);
    return list.length ? list : [''];
  }, [product]);

  // Only enable loop if there are enough slides (more than 1)
  const shouldLoop = photos.length > 1;

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: `4 / 3`,
        borderRadius: '16px',
        borderStyle: 'solid',
        borderWidth: '2px',
        borderColor: theme.palette.primary.main,
        overflow: 'hidden',
        position: 'relative',
        ...sx,
      }}
    >
      {pending ? (
        <Box
          role="status"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            bgcolor: 'rgba(255, 255, 255, 0.78)',
            color: 'text.primary',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Box component="i" className="fa-solid fa-spinner fa-spin" />
          Gathering photos and details…
        </Box>
      ) : null}
      <Swiper
        pagination
        loop={shouldLoop}
        slidesPerView={1}
        slidesPerGroup={1}
        modules={[Pagination]}
      >
        {photos.map((photo, idx) => (
          <SwiperSlide key={idx}>
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CommonImage photo={photo ?? ''} />
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};
